import { useContext } from 'react';
import { QueryFunctionContext, useQuery } from 'react-query';
import { lock_api } from '@axo/shared/data-access/api';
import {
  DataAccessContext,
  IDataAccessContext,
  getAPI,
  useAPI,
} from '@axo/shared/data-access/provider';
import { queryFunction } from '../../query-function';
import { lock } from '@axo/shared/data-access/types';
import {
  applicationKeys,
  parseApplicationKey,
} from '../../cache-keys/applicationKeys';

function params({
  queryKey,
}: QueryFunctionContext<ReturnType<(typeof applicationKeys)['locks']>>) {
  const { applicationID, marketCountry } = parseApplicationKey(queryKey);
  return { country: marketCountry, loanApplicationID: applicationID };
}

function getSingleLock(data: lock.FindResult): lock.Lock | null {
  return data.Items.length < 1 ? null : data.Items[0];
}

export function useApplicationLock(
  applicationID?: string,
  marketCountry?: string
) {
  const host = useAPI();
  const cacheKey = applicationKeys.locks({
    applicationID,
    marketCountry,
  });

  return useQuery(cacheKey, queryFunction(lock_api.find(host), params), {
    enabled: applicationID !== undefined && marketCountry !== undefined,
    select: getSingleLock,
  });
}

export function useCurrentUserHasLock(
  applicationID?: string,
  marketCountry?: string
) {
  const {
    state: {
      user: { clientID },
    },
  } = useContext(DataAccessContext);
  const api = useAPI();

  const cacheKey = applicationKeys.locks({
    applicationID,
    marketCountry,
  });

  return useQuery(cacheKey, queryFunction(lock_api.find(api), params), {
    enabled: applicationID !== undefined && marketCountry !== undefined,
    select: (data) =>
      data.Items.find((x) => x.LockedBy === clientID) !== undefined,
  });
}

export async function fetchApplicationLock(
  context: IDataAccessContext,
  marketCountry: string,
  applicationID: string
) {
  const cacheKey = applicationKeys.locks({
    applicationID,
    marketCountry,
  });
  const findResult = await context.queryClient?.fetchQuery({
    queryKey: cacheKey,
    queryFn: queryFunction(lock_api.find(getAPI(context)), params),
  });
  if (!findResult) return null;
  return getSingleLock(findResult);
}
